<script setup lang="ts">
import device from '@@/bits/device'
import { defineAsyncComponent } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useAutoScrollElement } from '@@/pragmatic-drag-and-drop/useAutoScrollElement'
import type { Id } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import SurfacePostsMatrix from '@@/vuecomponents/SurfacePostsMatrix.vue'
import {
  SURFACE_MATRIX_POST_DRAGGABLE_TYPE,
  SURFACE_POSTS_MATRIX_SECTION_HEADER,
} from '@@/vuecomponents/SurfacePostsMatrixSection.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { useCollapseAroundTouchPointOnDragStart } from '@@/vuecomposables/useCollapseAroundTouchPointOnDragStart'
import { storeToRefs } from 'pinia'
import { onMounted, ref } from 'vue'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

const { xHeader, backgroundLuminance, isReadOnly, isPostSizeWide, isPostSizeStandard, isFrozen } =
  storeToRefs(useSurfaceStore())
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { isGroupBySection, isGroupBySingleSelect, sortedGroups } = storeToRefs(useSurfaceWishArrangementStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())

if (!device.mobile) useDoubleClickToPost()

const isReadyToMount = ref(false)
onMounted(() => {
  setTimeout(() => {
    isReadyToMount.value = true
  }, 0)
})

const containerRef = ref<HTMLDivElement | null>(null)
useAutoScrollElement({
  scrollContainerElementRef: containerRef,
  type: [SURFACE_POSTS_MATRIX_SECTION_HEADER, SURFACE_MATRIX_POST_DRAGGABLE_TYPE],
})

const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()
const { isAPostDragging } = storeToRefs(surfacePostsDragAndDropStore)
const postsContainerDragPadding = ref<{ paddingTop?: string; paddingBottom?: string }>({})

useCollapseAroundTouchPointOnDragStart({
  scrollContainerElementRef: containerRef,
  postContainerDragPaddingRef: postsContainerDragPadding,
  triggerRef: isAPostDragging,
})
</script>

<template>
  <div
    id="wall-container"
    ref="containerRef"
    :class="{
      'scrollbar-photo': backgroundLuminance === 'light',
      'scrollbar-photo-dark': backgroundLuminance === 'dark',
      'overflow-x-hidden': !isReadOnly,
    }"
    @scroll="$emit('scroll', $event)"
  >
    <SurfaceHeader v-if="xHeader" />

    <template v-if="isReadyToMount">
      <SurfacePostsMatrix
        :class="{
          'pb-8 @tablet-portrait/surface-container:pb-12': isGroupBySection || isGroupBySingleSelect,
        }"
        :reaction-ids-by-post-id="reactionIdsByPostId"
        :comment-ids-by-post-id="commentIdsByPostId"
        :groups="sortedGroups"
        :style="postsContainerDragPadding"
      />
      <div
        v-if="(isGroupBySection || isGroupBySingleSelect) && canIModerate && !isReadOnly && !isFrozen"
        :class="[
          'flex',
          'flex-col',
          'items-center',
          'mx-auto',
          'box-border',
          'w-full',
          isPostSizeStandard &&
            '@tablet-portrait/surface-container:max-w-[1136px] @tablet-landscape/surface-container:max-w-[1184px] @desktop/surface-container:max-w-[1088px] @tablet-portrait/surface-container:px-6 @tablet-landscape/surface-container:px-12 @desktop/surface-container:px-0',
          isPostSizeWide &&
            '@tablet-portrait/surface-container:max-w-[1236px] @desktop/surface-container:max-w-[1680px] @tablet-portrait/surface-container:px-6 @desktop/surface-container:px-12',
          'pb-16',
        ]"
      >
        <div v-if="!isDemoPadletPanelDesktop" class="w-full">
          <SurfaceAddSectionButton class="ms-4 @tablet-portrait/surface-container:ms-0" />
        </div>
      </div>
    </template>
  </div>
</template>
